import React, { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { UploadCloud, Download, File as FileIcon, Loader2, ChevronRight, Sparkles, Lightbulb, CheckCircle2 } from 'lucide-react';
import { CONCEPT_ICONS } from '../utils/concept-icons';
import { useLocale } from '../contexts/LocaleContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { cn } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useErrorHandler } from '../contexts/ErrorHandlerContext';
import { fetchCreditBalance, hasEnoughCredits } from '../utils/credit-check';
import type { ImportFileResult } from '../hooks/useFileImport';
import { getFirstChapterScope, FIRST_CHAPTER_SCOPE_LABEL_KEYS } from '../utils/first-chapter';
import type {
  ImportAnalysisCreditEstimates,
  ImportAnalysisSelection,
  ImportAnalysisTriggerSource,
  RunImportAnalysesParams,
} from '../utils/import-analyses';
import { fetchImportAnalysisCreditEstimates } from '../utils/import-analyses';

type AnalysisId = 'style_calibration' | 'character_analysis' | 'location_analysis';
type AnalysisScope = 'first_chapter' | 'manuscript';

interface AnalysisOption {
  id: AnalysisId;
  icon: LucideIcon;
  titleKey: string;
  descriptionKey: string;
  scope: AnalysisScope;
  triggerSource: ImportAnalysisTriggerSource;
}

interface WizardImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<ImportFileResult>;
  onFinish: (params: RunImportAnalysesParams) => Promise<void>;
  isImporting: boolean;
}

const STEP_LABEL_KEYS = ['import_file', 'wizard_import_step_analysis'] as const;
const IMPORT_FILE_ACCEPT = '.docx,.doc,.odt,.pdf,.epub';

const ANALYSIS_OPTIONS: AnalysisOption[] = [
  {
    id: 'style_calibration',
    icon: Sparkles,
    titleKey: 'wizard_import_analysis_style_title',
    descriptionKey: 'wizard_import_analysis_style_description',
    scope: 'first_chapter',
    triggerSource: 'feedback',
  },
  {
    id: 'character_analysis',
    icon: CONCEPT_ICONS.character,
    titleKey: 'wizard_import_analysis_characters_title',
    descriptionKey: 'wizard_import_analysis_characters_description',
    scope: 'manuscript',
    triggerSource: 'characters',
  },
  {
    id: 'location_analysis',
    icon: CONCEPT_ICONS.location,
    titleKey: 'wizard_import_analysis_places_title',
    descriptionKey: 'wizard_import_analysis_places_description',
    scope: 'manuscript',
    triggerSource: 'locations',
  },
];

export const WizardImportModal: React.FC<WizardImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  onFinish,
  isImporting,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const { showCreditLimitModal } = useErrorHandler();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedAnalyses, setSelectedAnalyses] = useState<AnalysisId[]>(
    ANALYSIS_OPTIONS.map((option) => option.id)
  );
  const [firstChapterWordCount, setFirstChapterWordCount] = useState<number | null>(null);
  const [creditEstimates, setCreditEstimates] = useState<ImportAnalysisCreditEstimates | null>(null);
  const [isLoadingEstimates, setIsLoadingEstimates] = useState(false);
  const [importedDocumentId, setImportedDocumentId] = useState<string | null>(null);
  const [importedProjectId, setImportedProjectId] = useState<string | null>(null);
  const [suggestionsText, setSuggestionsText] = useState('');
  const [isFinishing, setIsFinishing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setSelectedFile(null);
      setIsDragging(false);
      setSelectedAnalyses(ANALYSIS_OPTIONS.map((option) => option.id));
      setFirstChapterWordCount(null);
      setCreditEstimates(null);
      setIsLoadingEstimates(false);
      setImportedDocumentId(null);
      setImportedProjectId(null);
      setSuggestionsText('');
      setIsFinishing(false);
    }
  }, [isOpen]);

  const getOptionTitle = (option: AnalysisOption): string => {
    if (option.id === 'style_calibration') {
      const scope = getFirstChapterScope(firstChapterWordCount);
      return `${t(option.titleKey)} - ${t(FIRST_CHAPTER_SCOPE_LABEL_KEYS[scope])}`;
    }
    return t(option.titleKey);
  };

  const getOptionCredits = (option: AnalysisOption): number => {
    return creditEstimates?.[option.triggerSource] ?? 0;
  };

  const toggleAnalysis = (id: AnalysisId): void => {
    setSelectedAnalyses((prev) =>
      prev.includes(id) ? prev.filter((analysisId) => analysisId !== id) : [...prev, id]
    );
  };

  const totalCredits = ANALYSIS_OPTIONS.filter((option) =>
    selectedAnalyses.includes(option.id)
  ).reduce((sum, option) => sum + getOptionCredits(option), 0);

  const openFilePicker = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0] ?? null;
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async (): Promise<void> => {
    if (!selectedFile || isImporting) {
      return;
    }
    const result = await onImport(selectedFile);
    if (result.success) {
      setFirstChapterWordCount(result.firstChapterWordCount);
      setSuggestionsText(result.suggestionsText);
      setImportedDocumentId(result.documentId);
      setImportedProjectId(result.projectId);
      setCurrentStep(1);
      if (token) {
        setIsLoadingEstimates(true);
        setCreditEstimates(null);
        try {
          const estimates = await fetchImportAnalysisCreditEstimates(token, {
            suggestionsText: result.suggestionsText,
            manuscriptText: result.manuscriptText,
          });
          setCreditEstimates(estimates);
        } catch {
          setCreditEstimates({
            feedback: 0,
            characters: 0,
            locations: 0,
          });
        } finally {
          setIsLoadingEstimates(false);
        }
      }
    }
  };

  const hasSelectedAnalyses = selectedAnalyses.length > 0;

  const handleFinish = async (): Promise<void> => {
    if (!importedDocumentId || isFinishing) {
      return;
    }
    const selection: ImportAnalysisSelection = {
      suggestions: selectedAnalyses.includes('style_calibration'),
      characters: selectedAnalyses.includes('character_analysis'),
      locations: selectedAnalyses.includes('location_analysis'),
    };

    if (hasSelectedAnalyses && token) {
      const balance = await fetchCreditBalance(token);
      if (balance !== null && !hasEnoughCredits(balance, totalCredits)) {
        showCreditLimitModal();
        return;
      }
    }

    setIsFinishing(true);
    try {
      await onFinish({
        documentId: importedDocumentId,
        projectId: importedProjectId,
        suggestionsText,
        selection,
      });
    } finally {
      setIsFinishing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isImporting && !isFinishing && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('wizard_import_title')}</DialogTitle>
        </DialogHeader>

        <div className="flex overflow-hidden rounded-lg border">
          {STEP_LABEL_KEYS.map((labelKey, index) => {
            const isActive = index === currentStep;
            return (
              <div
                key={labelKey}
                className={cn(
                  'flex flex-1 items-center gap-3 px-4 py-3',
                  index < STEP_LABEL_KEYS.length - 1 && 'border-r',
                  isActive ? 'border-b-2 border-b-foreground bg-muted/40' : 'bg-[#F9FAFB]'
                )}
              >
                <span
                  className={cn(
                    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                    isActive ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'
                  )}
                >
                  {index + 1}
                </span>
                <div className="flex flex-col">
                  <span className={cn('text-sm font-medium', !isActive && 'text-muted-foreground')}>
                    {t(labelKey)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t('wizard_import_step')} {index + 1} {t('wizard_import_of')} {STEP_LABEL_KEYS.length}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {currentStep === 0 ? (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-muted-foreground pt-6">{t('wizard_import_upload_description')}</p>

            <div
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors',
                selectedFile
                  ? 'border-green-400 bg-green-50 cursor-default'
                  : isDragging
                    ? 'border-foreground bg-muted/50'
                    : 'border-muted-foreground/25 hover:border-muted-foreground/75 hover:bg-[#F9FAFB]'
              )}
            >
              {selectedFile ? (
                <>
                  <CheckCircle2 className="mb-3 h-10 w-10 text-green-500" />
                  <p className="text-sm font-semibold text-green-800">{selectedFile.name}</p>
                  <p className="text-xs text-green-600 mt-1">{t('wizard_import_file_ready')}</p>
                </>
              ) : (
                <>
                  <UploadCloud className="mb-3 h-10 w-10 text-muted-foreground" />
                  <p className="text-sm font-medium">{t('wizard_import_dropzone_title')}</p>
                  <p className="text-xs text-muted-foreground">{t('wizard_import_dropzone_subtitle')}</p>
                </>
              )}
            </div>

            {selectedFile ? (
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors mx-auto"
                onClick={openFilePicker}
              >
                {t('wizard_import_change_file')}
              </button>
            ) : (
              <Button type="button" variant="outline" className="w-full border-muted-foreground/25 hover:border-muted-foreground/25 hover:bg-[#F9FAFB]" onClick={openFilePicker}>
                <Download className="mr-2 h-4 w-4" />
                {t('wizard_import_attach_from_computer')}
              </Button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={IMPORT_FILE_ACCEPT}
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-2">
            <p className="text-sm text-muted-foreground pt-6">{t('wizard_import_analysis_description')}</p>

            <div className="flex flex-col gap-3">
              {ANALYSIS_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isChecked = selectedAnalyses.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleAnalysis(option.id)}
                    disabled={isFinishing}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors',
                      isChecked ? 'border-foreground' : 'border-border hover:border-muted-foreground/50',
                      isFinishing && 'cursor-not-allowed opacity-60'
                    )}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleAnalysis(option.id)}
                      onClick={(event) => event.stopPropagation()}
                      disabled={isFinishing}
                      className="mt-1"
                    />
                    <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-sm font-medium">{getOptionTitle(option)}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {isLoadingEstimates ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              {getOptionCredits(option)} {t('wizard_import_credits_abbrev')}
                            </>
                          )}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{t(option.descriptionKey)}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">{t('wizard_import_ai_disclaimer')}</p>
              <p className="shrink-0 text-sm text-muted-foreground">
                {t('wizard_import_total')}:{' '}
                <span className="font-semibold text-foreground">
                  {isLoadingEstimates ? (
                    <Loader2 className="inline h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      {totalCredits} {t('wizard_import_credits')}
                    </>
                  )}
                </span>
              </p>
            </div>
          </div>
        )}

        {currentStep === 0 ? (
          <DialogFooter className="mt-8">
            <Button
              type="button"
              data-dialog-primary
              onClick={() => void handleImport()}
              disabled={!selectedFile || isImporting}
            >
              {isImporting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isImporting ? t('importing') : t('wizard_import_submit')}
              {!isImporting && <ChevronRight className="ml-1 h-4 w-4" />}
            </Button>
          </DialogFooter>
        ) : (
          <DialogFooter className="sm:justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCurrentStep(0)}
              disabled={isFinishing}
            >
              {t('wizard_import_back')}
            </Button>
            <Button
              type="button"
              data-dialog-primary
              onClick={() => void handleFinish()}
              disabled={isFinishing || (hasSelectedAnalyses && isLoadingEstimates)}
            >
              {isFinishing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                hasSelectedAnalyses && <Lightbulb className="mr-2 h-4 w-4" />
              )}
              {hasSelectedAnalyses
                ? t('wizard_import_analyze_and_finish')
                : t('wizard_import_finish')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
